import React, { createContext, useContext, useState, useEffect } from 'react';
import { signUp, signIn, signOut, getCurrentUser, supabase } from '../../utils/supabase';

// 创建Auth上下文
const AuthContext = createContext();

// 使用Auth上下文的Hook
export function useAuth() {
  return useContext(AuthContext);
}

// 检查Supabase配置是否有效
function isSupabaseConfigValid() {
  const url = process.env.REACT_APP_SUPABASE_URL || '';
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
  return !(url.includes('你的项目URL') || key.includes('你的匿名密钥') || url === '' || key === '');
}

// 初始化默认访客用户状态 - 防止认证错误
const guestUserState = {
  id: 'guest-user',
  email: null,
  created_at: new Date().toISOString(),
  user_metadata: {
    displayName: '访客用户',
    testCount: 0,
    valid_subscription: true, // 默认开启所有功能
    isAdmin: false,
    createdAt: new Date().toISOString()
  }
};

// Auth提供者组件
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(guestUserState); // 使用默认访客状态
  const [userData, setUserData] = useState({
    ...guestUserState,
    ...guestUserState.user_metadata,
    isAdmin: false
  });
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [usingLocalMode, setUsingLocalMode] = useState(false);

  // 错误处理函数
  const handleAuthError = (error) => {
    console.error('认证错误:', error);
    
    // 错误消息对应表
    const errorMessages = {
      'invalid-email': '邮箱格式不正确',
      'weak-password': '密码强度太弱，请设置更复杂的密码',
      'email-already-in-use': '该邮箱已被注册',
      'user-not-found': '找不到该用户账号',
      'wrong-password': '密码不正确',
      'too-many-requests': '操作次数过多，请稍后再试',
      'network-error': '网络连接问题，请检查您的网络',
      'supabase-config-invalid': 'Supabase配置无效，已切换到本地模式'
    };
    
    // 获取友好错误信息
    const errorCode = error.code || (error.message ? 'unknown-error' : 'unknown-error');
    const friendlyMessage = errorMessages[errorCode] || error.message || '发生未知错误';
    
    setAuthError(friendlyMessage);
    return { success: false, error: friendlyMessage };
  };

  // 注册新用户 - 使用Supabase
  async function register(email, password) {
    try {
      setAuthError(null);
      
      // 检查邮箱格式
      if (!email || !email.includes('@')) {
        return handleAuthError({ code: 'invalid-email' });
      }
      
      // 检查密码强度
      if (!password || password.length < 6) {
        return handleAuthError({ code: 'weak-password' });
      }
      
      // 检查Supabase配置
      if (!isSupabaseConfigValid()) {
        console.warn('Supabase配置无效，使用本地模式创建用户');
        setUsingLocalMode(true);
        
        // 创建本地用户
        const mockUser = {
          id: `local-${Date.now()}`,
          email: email,
          created_at: new Date().toISOString(),
          user_metadata: {
            displayName: email.split('@')[0],
            testCount: 0,
            valid_subscription: true, // 默认开启所有功能
            isAdmin: email.toLowerCase().includes('admin'),
            createdAt: new Date().toISOString()
          }
        };
        
        // 保存到localStorage
        localStorage.setItem('localUser', JSON.stringify(mockUser));
        
        // 设置用户状态
        const isAdminUser = email.toLowerCase().includes('admin');
        setCurrentUser(mockUser);
        setUserData({
          ...mockUser,
          ...mockUser.user_metadata,
          isAdmin: isAdminUser
        });
        setIsAdmin(isAdminUser);
        
        return { success: true };
      }
      
      // 用户元数据
      const userMetadata = {
        displayName: email.split('@')[0],
        testCount: 0,
        valid_subscription: true, // 默认开启所有功能
        isAdmin: email.toLowerCase().includes('admin'),
        createdAt: new Date().toISOString()
      };
      
      // 调用Supabase注册API
      const { data, error } = await signUp(email, password, userMetadata);
      
      if (error) {
        return handleAuthError(error);
      }
      
      // 设置用户状态
      if (data && data.user) {
        const isAdminUser = email.toLowerCase().includes('admin');
        setCurrentUser(data.user);
        setUserData({
          ...data.user,
          ...userMetadata,
          isAdmin: isAdminUser
        });
        setIsAdmin(isAdminUser);
      }
      
      console.log('注册成功:', data?.user);
      return { success: true };
    } catch (error) {
      return handleAuthError(error);
    }
  }

  // 登录现有用户 - 使用Supabase
  async function login(email, password) {
    try {
      setAuthError(null);
      
      // 检查邮箱格式
      if (!email || !email.includes('@')) {
        return handleAuthError({ code: 'invalid-email' });
      }
      
      // 检查密码
      if (!password) {
        return handleAuthError({ code: 'wrong-password' });
      }
      
      // 检查Supabase配置
      if (!isSupabaseConfigValid()) {
        console.warn('Supabase配置无效，使用本地模式登录');
        setUsingLocalMode(true);
        
        // 尝试从localStorage获取用户
        const localUser = localStorage.getItem('localUser');
        if (localUser) {
          const user = JSON.parse(localUser);
          if (user.email === email) {
            // 登录成功
            const isAdminUser = email.toLowerCase().includes('admin');
            setCurrentUser(user);
            setUserData({
              ...user,
              displayName: user.user_metadata?.displayName || email.split('@')[0],
              testCount: user.user_metadata?.testCount || 0,
              valid_subscription: user.user_metadata?.valid_subscription || isAdminUser,
              isAdmin: isAdminUser,
              createdAt: user.user_metadata?.createdAt || new Date().toISOString()
            });
            setIsAdmin(isAdminUser);
            
            return { success: true };
          } else {
            // 邮箱不匹配
            return handleAuthError({ code: 'wrong-password' });
          }
        } else {
          // 未找到本地用户
          return handleAuthError({ code: 'user-not-found' });
        }
      }
      
      // 调用Supabase登录API
      const { data, error } = await signIn(email, password);
      
      if (error) {
        return handleAuthError(error);
      }
      
      // 设置用户状态
      if (data && data.user) {
        const isAdminUser = email.toLowerCase().includes('admin');
        const user = data.user;
        
        // 获取用户元数据或使用默认值
        const metadata = user.user_metadata || {};
        
        setCurrentUser(user);
        setUserData({
          ...user,
          displayName: metadata.displayName || email.split('@')[0],
          testCount: metadata.testCount || 0,
          valid_subscription: metadata.valid_subscription || isAdminUser,
          isAdmin: isAdminUser,
          createdAt: metadata.createdAt || new Date().toISOString()
        });
        setIsAdmin(isAdminUser);
      }
      
      console.log('登录成功:', data?.user);
      return { success: true };
    } catch (error) {
      return handleAuthError(error);
    }
  }

  // 退出登录 - 使用Supabase
  async function logout() {
    try {
      setAuthError(null);
      
      if (usingLocalMode) {
        // 本地模式下直接清除状态
        setCurrentUser(guestUserState);
        setUserData(guestUserState);
        setIsAdmin(false);
        setUsingLocalMode(false);
        return { success: true };
      }
      
      // 调用Supabase登出API
      const { error } = await signOut();
      
      if (error) {
        return handleAuthError(error);
      }
      
      // 清除用户状态
      setCurrentUser(guestUserState);
      setUserData(guestUserState);
      setIsAdmin(false);
      
      console.log('已退出登录');
      return { success: true };
    } catch (error) {
      return handleAuthError(error);
    }
  }
  
  // 密码重置 - 使用Supabase
  async function resetPassword(email) {
    try {
      setAuthError(null);
      
      if (usingLocalMode) {
        // 本地模式下无法重置密码
        console.warn('本地模式下无法重置密码');
        return { success: false, error: '本地模式下无法重置密码' };
      }
      
      // 检查邮箱格式
      if (!email || !email.includes('@')) {
        return handleAuthError({ code: 'invalid-email' });
      }
      
      // 检查Supabase配置
      if (!isSupabaseConfigValid()) {
        return handleAuthError({ code: 'supabase-config-invalid' });
      }
      
      // 调用Supabase密码重置API
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      
      if (error) {
        return handleAuthError(error);
      }
      
      console.log('密码重置邮件已发送到:', email);
      return { success: true, message: '密码重置链接已发送到您的邮箱' };
    } catch (error) {
      return handleAuthError(error);
    }
  }
  
  // 更新用户邮箱 - 使用Supabase
  async function updateUserEmail(newEmail) {
    try {
      setAuthError(null);
      
      if (!currentUser) {
        return { success: false, error: '未登录' };
      }
      
      // 检查邮箱格式
      if (!newEmail || !newEmail.includes('@')) {
        return handleAuthError({ code: 'invalid-email' });
      }
      
      // 调用Supabase更新邮箱API
      const { data, error } = await supabase.auth.updateUser({
        email: newEmail
      });
      
      if (error) {
        return handleAuthError(error);
      }
      
      if (data && data.user) {
        // 更新本地用户状态
        setCurrentUser(data.user);
        setUserData({
          ...userData,
          ...data.user
        });
      }
      
      console.log('邮箱已更新:', newEmail);
      return { success: true, message: '邮箱已更新，请查收验证邮件' };
    } catch (error) {
      return handleAuthError(error);
    }
  }
  
  // 更新用户密码 - 使用Supabase
  async function updateUserPassword(newPassword) {
    try {
      setAuthError(null);
      
      if (!currentUser) {
        return { success: false, error: '未登录' };
      }
      
      // 检查密码强度
      if (!newPassword || newPassword.length < 6) {
        return handleAuthError({ code: 'weak-password' });
      }
      
      // 调用Supabase更新密码API
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        return handleAuthError(error);
      }
      
      console.log('密码已更新');
      return { success: true, message: '密码已成功更新' };
    } catch (error) {
      return handleAuthError(error);
    }
  }
  
  // 更新用户元数据 - 使用Supabase
  async function updateUserMetadata(newMetadata) {
    try {
      setAuthError(null);
      
      if (!currentUser) {
        return { success: false, error: '未登录' };
      }
      
      // 调用Supabase更新元数据API
      const { data, error } = await supabase.auth.updateUser({
        user_metadata: newMetadata
      });
      
      if (error) {
        return handleAuthError(error);
      }
      
      if (data && data.user) {
        // 更新本地用户状态
        setCurrentUser(data.user);
        setUserData({
          ...userData,
          ...data.user
        });
      }
      
      console.log('用户元数据已更新');
      return { success: true, message: '用户元数据已成功更新' };
    } catch (error) {
      return handleAuthError(error);
    }
  }
  
  // 初始化 - 检查用户会话
  useEffect(() => {
    let isMounted = true; // 防止组件卸载后状态更新
    
    // 检查用户认证状态
    async function checkUserAuth() {
      try {
        if (!isMounted) return;
        setLoading(true);
        
        // 检查Supabase配置
        if (!isSupabaseConfigValid()) {
          console.warn('Supabase配置无效，使用本地模式');
          if (!isMounted) return;
          setUsingLocalMode(true);
          
          // 尝试从localStorage获取用户
          const localUser = localStorage.getItem('localUser');
          if (localUser) {
            if (!isMounted) return;
            const userData = JSON.parse(localUser);
            const isAdminUser = userData.email && userData.email.toLowerCase().includes('admin');
            
            setCurrentUser(userData);
            setUserData({
              ...userData,
              displayName: userData.user_metadata?.displayName || (userData.email ? userData.email.split('@')[0] : '访客用户'),
              testCount: userData.user_metadata?.testCount || 0,
              valid_subscription: userData.user_metadata?.valid_subscription || isAdminUser,
              isAdmin: isAdminUser,
              createdAt: userData.user_metadata?.createdAt || new Date().toISOString()
            });
            setIsAdmin(isAdminUser);
            console.log('已恢复本地用户会话:', userData);
          }
          
          if (!isMounted) return;
          setLoading(false);
          return;
        }
        
        // 避免在服务器端渲染时尝试访问浏览器API
        if (typeof window === 'undefined') {
          if (!isMounted) return;
          setLoading(false);
          return;
        }
        
        // 尝试获取当前Supabase用户
        const { user, error } = await getCurrentUser();
        
        if (!isMounted) return;
        
        if (error || !user) {
          console.log('未找到用户会话或会话已过期');
          
          // 尝试从localStorage获取用户作为后备
          const localUser = localStorage.getItem('localUser');
          if (localUser) {
            console.log('找到本地用户数据，切换到本地模式');
            setUsingLocalMode(true);
            const userData = JSON.parse(localUser);
            const isAdminUser = userData.email && userData.email.toLowerCase().includes('admin');
            
            setCurrentUser(userData);
            setUserData({
              ...userData,
              displayName: userData.user_metadata?.displayName || (userData.email ? userData.email.split('@')[0] : '访客用户'),
              testCount: userData.user_metadata?.testCount || 0,
              valid_subscription: userData.user_metadata?.valid_subscription || isAdminUser,
              isAdmin: isAdminUser,
              createdAt: userData.user_metadata?.createdAt || new Date().toISOString()
            });
            setIsAdmin(isAdminUser);
          }
          
          setLoading(false);
          return;
        }
        
        // 设置用户状态
        const isAdminUser = user.email && user.email.toLowerCase().includes('admin');
        
        // 获取用户元数据或使用默认值
        const metadata = user.user_metadata || {};
        
        setCurrentUser(user);
        setUserData({
          ...user,
          displayName: metadata.displayName || (user.email ? user.email.split('@')[0] : '访客用户'),
          testCount: metadata.testCount || 0,
          valid_subscription: metadata.valid_subscription || isAdminUser,
          isAdmin: isAdminUser,
          createdAt: metadata.createdAt || new Date().toISOString()
        });
        setIsAdmin(isAdminUser);
        
        console.log('已恢复用户会话:', user);
      } catch (error) {
        if (!isMounted) return;
        console.error('恢复会话失败:', error);
        
        // 尝试从localStorage获取用户作为后备
        const localUser = localStorage.getItem('localUser');
        if (localUser) {
          console.log('找到本地用户数据，切换到本地模式');
          setUsingLocalMode(true);
          const userData = JSON.parse(localUser);
          const isAdminUser = userData.email && userData.email.toLowerCase().includes('admin');
          
          setCurrentUser(userData);
          setUserData({
            ...userData,
            displayName: userData.user_metadata?.displayName || (userData.email ? userData.email.split('@')[0] : '访客用户'),
            testCount: userData.user_metadata?.testCount || 0,
            valid_subscription: userData.user_metadata?.valid_subscription || isAdminUser,
            isAdmin: isAdminUser,
            createdAt: userData.user_metadata?.createdAt || new Date().toISOString()
          });
          setIsAdmin(isAdminUser);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    
    checkUserAuth();
    
    return () => {
      isMounted = false; // 组件卸载时防止状态更新
    };
  }, []);

  // 可扩展的用户上下文值
  const value = {
    currentUser,
    userData,
    isAdmin,
    loading,
    authError,
    register,
    login,
    logout,
    updateUserMetadata,
    updateUserEmail,
    updateUserPassword,
    setAuthError,
    usingLocalMode,
    premiumStatus: true // 默认所有用户都有高级权限
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext; 